/**
 * Advertising placements. Every creative shows its disclosure ("Ad", "Sponsored", "Illustrative …"), impressions are
 * counted only after the creative is at least half visible for one second, and clicks only navigate inside the app.
 * Placements never appear in cart, checkout, payment, tracking or verification — browsing surfaces only.
 */
import type { ProductCardDto } from '@runnerboy/types';
import { cx } from '@runnerboy/ui';
import { ArrowRight, ChevronRight } from 'lucide-react';
import { useEffect, useRef, useState, type CSSProperties, type ReactNode } from 'react';
import { useNavigate } from 'react-router';
import { client, useAsync, useSession } from '../lib/session';
import { Art } from './art';
import { ProductCard, stageStyle, useCartActions } from './commerce';

export interface ServedAd {
  id: string;
  campaignId: string;
  placement: string;
  title: string;
  subtitle: string | null;
  body: string | null;
  ctaLabel: string | null;
  ctaPath: string | null;
  imageUrl: string | null;
  artKey: string | null;
  theme: { bg?: string; fg?: string; accent?: string } | null;
  productIds: string[];
  disclosure: string;
  illustrative: boolean;
  advertiser: string;
  isDemo: boolean;
  token: string;
}
type AdMap = Record<string, ServedAd[]>;

// ---- event batching ---------------------------------------------------------------------------------------------
type AdEvent = { creativeId: string; token: string; type: 'IMPRESSION' | 'CLICK' };
const queue: AdEvent[] = [];
let timer: ReturnType<typeof setTimeout> | null = null;
function track(e: AdEvent) {
  queue.push(e);
  if (e.type === 'CLICK') return flush();
  if (!timer) timer = setTimeout(flush, 2500);
}
function flush() {
  if (timer) clearTimeout(timer);
  timer = null;
  if (queue.length === 0) return;
  const events = queue.splice(0, 50);
  void client.post('/public/ads/events', { events }).catch(() => undefined);
}
if (typeof window !== 'undefined') window.addEventListener('pagehide', flush);

export function useAds(placements: string[], opts: { storeId?: string | null; category?: string | null; enabled?: boolean } = {}) {
  const { location, ageGate } = useSession();
  const qs = new URLSearchParams({ placements: placements.join(','), age: ageGate === 'confirmed' ? '1' : '0' });
  if (location) {
    qs.set('lat', String(location.lat));
    qs.set('lng', String(location.lng));
  }
  if (opts.storeId) qs.set('storeId', opts.storeId);
  if (opts.category) qs.set('category', opts.category);
  const key = qs.toString();
  const r = useAsync((s) => client.get<AdMap>(`/public/ads?${key}`, s), [key], { enabled: opts.enabled !== false });
  return r.data ?? {};
}

function useImpression(ad: ServedAd | undefined) {
  const ref = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const el = ref.current;
    if (!el || !ad || typeof IntersectionObserver === 'undefined') return;
    let t: ReturnType<typeof setTimeout> | null = null;
    let done = false;
    const io = new IntersectionObserver(
      ([entry]) => {
        if (done) return;
        if (entry && entry.intersectionRatio >= 0.5) t = setTimeout(() => ((done = true), track({ creativeId: ad.id, token: ad.token, type: 'IMPRESSION' })), 1000);
        else if (t) clearTimeout(t);
      },
      { threshold: [0, 0.5, 1] },
    );
    io.observe(el);
    return () => {
      io.disconnect();
      if (t) clearTimeout(t);
    };
  }, [ad]);
  return ref;
}

function useAdClick() {
  const nav = useNavigate();
  return (ad: ServedAd) => {
    track({ creativeId: ad.id, token: ad.token, type: 'CLICK' });
    if (ad.ctaPath && ad.ctaPath.startsWith('/')) nav(ad.ctaPath);
  };
}

// ---- visual helpers ---------------------------------------------------------------------------------------------------
const hexRgb = (hex: string) => {
  const n = parseInt(hex.replace('#', ''), 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255] as const;
};
const rgba = (hex: string, a: number) => {
  const [r, g, b] = hexRgb(hex);
  return `rgba(${r},${g},${b},${a})`;
};
const shade = (hex: string, f: number) => {
  const [r, g, b] = hexRgb(hex);
  const c = (v: number) => Math.max(0, Math.min(255, Math.round(f < 0 ? v * (1 + f) : v + (255 - v) * f)));
  return `rgb(${c(r)},${c(g)},${c(b)})`;
};
const isDark = (hex: string | undefined) => {
  if (!hex) return true;
  const [r, g, b] = hexRgb(hex);
  return r * 0.299 + g * 0.587 + b * 0.114 < 150;
};
/** Layered art direction from the creative's theme: base gradient, accent glow, soft counter-light. */
function layered(ad: ServedAd, fallbackBg = '#0E1324'): CSSProperties {
  const bg = ad.theme?.bg ?? fallbackBg;
  const accent = ad.theme?.accent ?? '#FFAA1F';
  const dark = isDark(bg);
  return {
    color: ad.theme?.fg ?? (dark ? '#FFFFFF' : '#0E1324'),
    background: [
      `radial-gradient(110% 130% at 100% 0%, ${rgba(accent, dark ? 0.38 : 0.3)} 0%, transparent 55%)`,
      `radial-gradient(80% 110% at 0% 100%, ${dark ? 'rgba(255,255,255,0.07)' : 'rgba(255,255,255,0.6)'} 0%, transparent 60%)`,
      `linear-gradient(140deg, ${shade(bg, dark ? 0.06 : 0.04)} 0%, ${shade(bg, dark ? -0.35 : -0.06)} 100%)`,
    ].join(','),
  };
}

/** Always-visible disclosure. Compact form keeps the first two parts (e.g. "Illustrative mockup · fictional brand");
 *  the full text stays available to assistive tech and on hover. */
function Disclosure({ ad, dark, full }: { ad: ServedAd; dark?: boolean; full?: boolean }) {
  const parts = ad.disclosure.split(' · ');
  const text = full ? ad.disclosure : parts.slice(0, 2).join(' · ');
  return (
    <span
      className={cx('rb-eyebrow inline-block max-w-full rounded-[8px] px-2 py-[3px] text-[9px] leading-snug tracking-[0.1em]', dark ? 'bg-white/[0.12] text-white/80' : 'bg-ink-900/[0.07] text-ink-600')}
      title={`${ad.disclosure} · ${ad.advertiser}`}
      aria-label={ad.disclosure}
    >
      {text}
    </span>
  );
}

function Cta({ ad, dark }: { ad: ServedAd; dark: boolean }) {
  if (!ad.ctaLabel) return null;
  const accent = ad.theme?.accent ?? '#FFAA1F';
  return (
    <span
      className="inline-flex w-fit items-center gap-1.5 rounded-full px-4 py-2 text-[13px] font-bold"
      style={{ background: accent, color: isDark(accent) ? '#FFFFFF' : '#0E1324', boxShadow: dark ? `0 8px 20px -8px ${rgba(accent, 0.7)}` : undefined }}
    >
      {ad.ctaLabel} <ArrowRight className="h-4 w-4" aria-hidden />
    </span>
  );
}

// ---- Large hero carousel -------------------------------------------------------------------------------------------------
export function HeroCarousel({ ads }: { ads: ServedAd[] }) {
  const [index, setIndex] = useState(0);
  const scroller = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (ads.length < 2) return;
    if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) return;
    const id = setInterval(() => {
      const el = scroller.current;
      if (!el) return;
      el.scrollTo({ left: ((index + 1) % ads.length) * el.clientWidth, behavior: 'smooth' });
    }, 5500);
    return () => clearInterval(id);
  }, [ads.length, index]);
  if (ads.length === 0) return null;
  return (
    <section aria-label="Featured" className="-mx-4">
      <div ref={scroller} className="rb-snap-x flex overflow-x-auto" onScroll={(e) => setIndex(Math.round(e.currentTarget.scrollLeft / Math.max(1, e.currentTarget.clientWidth)))}>
        {ads.map((ad) => (
          <div key={ad.id} className="w-full shrink-0 px-4">
            <HeroCard ad={ad} />
          </div>
        ))}
      </div>
      {ads.length > 1 && (
        <div className="mt-3 flex justify-center gap-1.5" aria-hidden>
          {ads.map((a, i) => (
            <span key={a.id} className={cx('h-1.5 rounded-full transition-all duration-300', i === index ? 'w-6 bg-ink-900' : 'w-1.5 bg-ink-300')} />
          ))}
        </div>
      )}
    </section>
  );
}

function HeroCard({ ad }: { ad: ServedAd }) {
  const ref = useImpression(ad);
  const click = useAdClick();
  const dark = isDark(ad.theme?.bg ?? '#0E1324');
  return (
    <div ref={ref}>
      <button onClick={() => click(ad)} className="rb-press rb-sheen relative block h-[212px] w-full overflow-hidden rounded-[28px] text-left shadow-[var(--shadow-raised)]" style={layered(ad)}>
        <span className="absolute -right-10 -top-10 h-40 w-40 rounded-full border border-white/10" aria-hidden />
        <span className="absolute -right-2 top-16 h-24 w-24 rounded-full border border-white/10" aria-hidden />
        <Art name={ad.artKey} accent={ad.theme?.accent} className="absolute -bottom-3 right-0 h-[82%] w-[54%] drop-shadow-[0_18px_22px_rgb(0_0_0/0.35)]" />
        <span className="relative flex h-full max-w-[62%] flex-col p-5">
          <Disclosure ad={ad} dark={dark} />
          <span className="rb-display mt-2.5 block text-[26px] font-extrabold leading-[1.0]">{ad.title}</span>
          {ad.subtitle && <span className={cx('mt-2 block text-[13.5px] font-medium leading-snug', dark ? 'text-white/80' : 'opacity-75')}>{ad.subtitle}</span>}
          <span className="mt-auto">
            <Cta ad={ad} dark={dark} />
          </span>
        </span>
      </button>
    </div>
  );
}

// ---- Horizontal card carousel ---------------------------------------------------------------------------------------------
export function PromoCarousel({ ads, title }: { ads: ServedAd[]; title?: ReactNode }) {
  if (ads.length === 0) return null;
  return (
    <section aria-label="Promotions">
      {title}
      <div className="rb-snap-x -mx-4 flex gap-3 overflow-x-auto px-4 pb-2">
        {ads.map((ad) => (
          <PromoCard key={ad.id} ad={ad} />
        ))}
      </div>
    </section>
  );
}

function PromoCard({ ad }: { ad: ServedAd }) {
  const ref = useImpression(ad);
  const click = useAdClick();
  const dark = isDark(ad.theme?.bg ?? '#0E1324');
  return (
    <div ref={ref} className="w-[256px] shrink-0">
      <button onClick={() => click(ad)} className="rb-press relative block h-[164px] w-full overflow-hidden rounded-[24px] p-4 text-left shadow-[var(--shadow-card)]" style={layered(ad)}>
        <Art name={ad.artKey} accent={ad.theme?.accent} className="absolute -bottom-3 -right-4 h-[104px] w-[140px] drop-shadow-[0_12px_16px_rgb(0_0_0/0.25)]" />
        <span className="relative block max-w-[66%]">
          <Disclosure ad={ad} dark={dark} />
          <span className="rb-display mt-2 block text-[18px] font-bold leading-[1.05]">{ad.title}</span>
          {ad.subtitle && <span className={cx('mt-1.5 block text-[12px] font-medium leading-snug', dark ? 'text-white/75' : 'opacity-70')}>{ad.subtitle}</span>}
        </span>
      </button>
    </div>
  );
}

// ---- Medium promotional banner --------------------------------------------------------------------------------------------
export function AdBanner({ ad }: { ad: ServedAd | undefined }) {
  const ref = useImpression(ad);
  const click = useAdClick();
  if (!ad) return null;
  const dark = isDark(ad.theme?.bg ?? '#0E1324');
  return (
    <div ref={ref}>
      <button onClick={() => click(ad)} className="rb-press relative flex min-h-[132px] w-full items-center overflow-hidden rounded-[26px] px-5 py-4 text-left shadow-[var(--shadow-card)]" style={layered(ad)}>
        <span className="relative z-10 block max-w-[60%]">
          <Disclosure ad={ad} dark={dark} />
          <span className="rb-display mt-2 block text-[20px] font-bold leading-[1.05]">{ad.title}</span>
          {ad.subtitle && <span className={cx('mt-1 block text-[12.5px] font-medium leading-snug', dark ? 'text-white/75' : 'opacity-75')}>{ad.subtitle}</span>}
        </span>
        <Art name={ad.artKey} accent={ad.theme?.accent} className="absolute bottom-0 right-9 h-[112px] w-[132px] drop-shadow-[0_14px_18px_rgb(0_0_0/0.22)]" />
        {ad.ctaLabel && (
          <span className="absolute bottom-3 right-3 grid h-9 w-9 place-items-center rounded-full bg-ink-900 text-white shadow-[var(--shadow-raised)]" aria-hidden>
            <ChevronRight className="h-5 w-5" />
          </span>
        )}
      </button>
    </div>
  );
}

// ---- Compact promotional strip -------------------------------------------------------------------------------------------------
export function AdStrip({ ad }: { ad: ServedAd | undefined }) {
  const ref = useImpression(ad);
  const click = useAdClick();
  if (!ad) return null;
  const dark = isDark(ad.theme?.bg ?? '#0E1324');
  return (
    <div ref={ref}>
      <button onClick={() => click(ad)} className="rb-press flex w-full items-center gap-3 overflow-hidden rounded-full py-1.5 pl-1.5 pr-4 text-left shadow-[var(--shadow-card)]" style={layered(ad)}>
        <span className="grid h-11 w-11 shrink-0 place-items-center rounded-full bg-white/15">
          <Art name={ad.artKey} accent={ad.theme?.accent} className="h-8 w-9" />
        </span>
        <span className="min-w-0 flex-1">
          <span className="block truncate text-[14px] font-bold">{ad.title}</span>
          {ad.subtitle && <span className={cx('block truncate text-[11.5px]', dark ? 'text-white/70' : 'opacity-70')}>{ad.subtitle}</span>}
        </span>
        <span className={cx('rb-eyebrow shrink-0 text-[9px]', dark ? 'text-white/60' : 'text-ink-500')}>{ad.disclosure.split(' ·')[0]}</span>
      </button>
    </div>
  );
}

// ---- Full-width feature (informational) --------------------------------------------------------------------------------------
export function FeatureBanner({ ad }: { ad: ServedAd | undefined }) {
  const ref = useImpression(ad);
  const click = useAdClick();
  if (!ad) return null;
  return (
    <div ref={ref}>
      <button onClick={() => click(ad)} className="rb-press flex w-full items-center gap-4 rounded-[26px] bg-white p-4 text-left shadow-[var(--shadow-card)]">
        <span className="grid h-[76px] w-[76px] shrink-0 place-items-center rounded-[22px] bg-peacock-50">
          <Art name={ad.artKey ?? 'checklist'} className="h-14 w-14" />
        </span>
        <span className="min-w-0 flex-1">
          <Disclosure ad={ad} />
          <span className="rb-display mt-1.5 block text-[17px] font-bold leading-tight text-ink-900">{ad.title}</span>
          {ad.subtitle && <span className="mt-0.5 block text-[12.5px] text-ink-500">{ad.subtitle}</span>}
        </span>
        <ChevronRight className="h-5 w-5 shrink-0 text-ink-400" aria-hidden />
      </button>
    </div>
  );
}

// ---- Scrolling announcements ------------------------------------------------------------------------------------------------
export function AnnouncementTicker({ ads, dark }: { ads: ServedAd[]; dark?: boolean }) {
  const ref = useImpression(ads[0]);
  if (ads.length === 0) return null;
  const items = [...ads, ...ads];
  return (
    <div ref={ref} className={cx('overflow-hidden rounded-full py-2', dark ? 'rb-glass' : 'bg-white shadow-[var(--shadow-card)]')} role="marquee" aria-label="Announcements">
      <div className="rb-ticker" style={{ ['--rb-ticker-duration' as string]: `${Math.max(26, ads.length * 10)}s` }}>
        {items.map((a, i) => (
          <span key={`${a.id}-${i}`} className={cx('flex shrink-0 items-center gap-2.5 px-5 text-[12.5px] font-medium', dark ? 'text-white/85' : 'text-ink-700')} aria-hidden={i >= ads.length}>
            <span className="h-1.5 w-1.5 rounded-full bg-marigold-500" aria-hidden />
            {a.title}
          </span>
        ))}
      </div>
    </div>
  );
}

// ---- Road-safety informational banner -------------------------------------------------------------------------------------
export function RoadSafetyStrip({ ad }: { ad: ServedAd | undefined }) {
  const ref = useImpression(ad);
  const click = useAdClick();
  if (!ad) return null;
  return (
    <div ref={ref}>
      <button onClick={() => click(ad)} className="rb-press flex w-full items-center gap-3.5 rounded-[26px] bg-night-900 p-4 text-left shadow-[var(--shadow-raised)]">
        <Art name={ad.artKey ?? 'shield'} className="h-14 w-14 shrink-0" />
        <span className="min-w-0 flex-1">
          <span className="rb-display block text-[18px] font-bold leading-tight text-marigold-500">{ad.title}</span>
          {ad.subtitle && <span className="mt-0.5 block text-[12.5px] text-white/70">{ad.subtitle}</span>}
        </span>
        <span className="grid h-9 w-9 shrink-0 place-items-center rounded-full bg-white/10 text-white" aria-hidden>
          <ChevronRight className="h-5 w-5" />
        </span>
      </button>
    </div>
  );
}

// ---- Sponsored products ------------------------------------------------------------------------------------------------------
export function SponsoredProducts({ ad, storeId }: { ad: ServedAd | undefined; storeId: string | null | undefined }) {
  const ref = useImpression(ad);
  const actions = useCartActions();
  const ids = ad?.productIds.join(',') ?? '';
  const items = useAsync((s) => client.get<{ items: ProductCardDto[] }>(`/public/stores/${storeId}/catalog?ids=${ids}&limit=12`, s), [storeId, ids], { enabled: !!ad && !!storeId && ids.length > 0 });
  if (!ad || !items.data || items.data.items.length === 0) return null;
  const first = items.data.items[0];
  return (
    <section ref={ref} aria-label={ad.title} className="-mx-4 px-4 py-5" style={first ? stageStyle(first.categorySlug) : undefined}>
      <div className="flex items-start justify-between gap-2">
        <div>
          {ad.subtitle && <div className="rb-eyebrow text-[10px] text-ink-500">{ad.subtitle}</div>}
          <h2 className="rb-display mt-1 text-[22px] font-bold text-ink-900">{ad.title}</h2>
        </div>
        <Disclosure ad={ad} />
      </div>
      <div className="rb-snap-x -mx-4 mt-3.5 flex gap-3 overflow-x-auto px-4 pb-1">
        {items.data.items.map((p) => (
          <div key={p.storeProductId} className="w-[156px] shrink-0">
            <ProductCard product={p} actions={actions} compact />
          </div>
        ))}
      </div>
    </section>
  );
}
